import sessionHelper from "./sessionHelper";
import { Client } from "./_clientService";

async function getChallenges(options: any) {
  try {
    let payload: any = {
      pageNo: options?.pageNo,
      limit: options?.pageSize,
      sortKey: "createdAt",
      sortDir: "DESC",
      search: options?.search,
      skipPagination: options?.skipPagination ?? false,
    };
    if (options?.type) payload.type = options.type;
    if (options?.tier) payload.tier = options.tier;
    if (options?.status !== undefined && options?.status !== "")
      payload.status = options.status;
    payload.expired = !!options?.expired;
    let path = "/challenges/list";
    const resp = await new Client({ path, payload }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function getChallengeInfo(challengeId: any) {
  try {
    const resp = await new Client({
      path: "/challenges/info",
      payload: { challengeId },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

export async function getConvertedRewardXP(rewardValue: number) {
  try {
    const resp = await new Client({
      path: "/challenges/get-converted-reward-xp",
      payload: { input: { rewardValue } },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function createChallenge(challengeDetails: any) {
  try {
    const resp = await new Client({
      path: "/challenges/create",
      payload: challengeDetails,
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function updateChallenge(challengeDetails: any, challengeId: any) {
  try {
    const resp = await new Client({
      path: "/challenges/update",
      payload: { ...challengeDetails, _id: challengeId },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

export async function deleteChallenge(challengeId: any) {
  const resp = await new Client({
    path: "/challenges/delete",
    payload: { challengeId },
  }).post();
  return resp;
}

export async function updateChallengeStatus(challengeId: any) {
  const resp = await new Client({
    path: "/challenges/update/status",
    payload: { challengeId },
  }).post();
  return resp;
}

// ─── Bulk upload ─────────────────────────────────────────────────────────

/** Uploads an "Individual" or "Combo" bulk-create XLSX. type = "individual" | "combo". */
export async function bulkUploadChallenges(type: "individual" | "combo", file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const resp = await new Client({
    path: `/challenges/bulk/${type}`,
    payload: formData,
    isFormData: true,
  }).post();
  return resp;
}

/** Downloads the bulk-create XLSX template. type = "individual" | "combo". */
export async function downloadChallengeTemplate(type: "individual" | "combo") {
  const url = `${import.meta.env.VITE_CMS_API_URL}/challenges/bulk/template/${type}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${sessionHelper?.token}` },
  });

  if (!response.ok) {
    throw new Error("Failed to download the template. Please try again.");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename=([^;]+)/);
  const filename = match
    ? match[1].trim()
    : `Bulk-Create-${type}-Challenges-Template.xlsx`;

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

// ─── Challenge reward default images ─────────────────────────────────────

export async function getChallengeRewardImages() {
  const resp = await new Client({ path: "/challenges/reward-images" }).get();
  return resp;
}

export async function upsertChallengeRewardImage(payload: {
  rewardType: string;
  fileType: string;
}) {
  const resp = await new Client({
    path: "/challenges/reward-images/upsert",
    payload,
  }).post();
  return resp;
}

export async function deleteChallengeRewardImage(rewardType: string) {
  const resp = await new Client({
    path: "/challenges/reward-images/delete",
    payload: { rewardType },
  }).post();
  return resp;
}

export async function copyChallenge(challengeId: any) {
  const resp = await new Client({
    path: `/challenges/copy`,
    payload: { input: { challengeId } },
  }).post();

  return resp;
}

export { getChallenges, getChallengeInfo, createChallenge, updateChallenge };
